import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useCallback, useState } from 'react';

import {
  downloadFromUrl,
  getFilenameFromHeaders,
} from 'src/shared/utils/download';
import { getErrorMessage } from 'src/shared/utils/errors';
import { request } from 'src/shared/utils/request';

interface Props {
  files: {
    url: string;
    name: string;
  }[];
  folderName: string;
}

/**
 * Custom hook for downloading an archive containing multiple files.
 *
 * @param {Props} options - The options for downloading the archive.
 * @param {Array<File>} options.files - The list of files to include in the archive.
 * @param {string} options.folderName - The name of the folder to create in the archive.
 *
 * @returns {Object} - An object containing the loading state, error message, and downloadArchive function.
 * @property {boolean} loading - Indicates whether the download is in progress.
 * @property {string | null} error - The error message, if any.
 * @property {Function} downloadArchive - The function to initiate the download of the archive.
 */
export const useDownloadArchive = ({ files, folderName }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadArchive = useCallback(async () => {
    setLoading(true);

    try {
      if (files.length === 1) {
        const { url, name } = files[0];
        await downloadFromUrl({ url, name, prefetch: true });
        return true;
      }

      const zip = new JSZip();
      const folder = zip.folder(folderName);

      await Promise.all(
        files.map(async file => {
          const { url, name } = file;

          if (url) {
            const data = await request(url, { credentials: 'include' }).then(
              r => ({
                file: r.blob(),
                name: getFilenameFromHeaders(r.headers) ?? name,
              }),
            );
            folder?.file(data.name, data.file);
          }
        }),
      );

      await zip
        .generateAsync({ type: 'blob' })
        .then(blob => saveAs(blob, folderName));
      return true;
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [files, folderName]);

  return { loading, error, downloadArchive };
};
